// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
    // XNG-Change
    printLabelOverride?: React.ReactNode;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

// XNG-Change
export interface ProductUnitOfMeasureSelectStyles {
    print?: InjectableCss;
    selectStyles?: SelectPresentationProps;
}

export const productUnitOfMeasureSelectStyles: ProductUnitOfMeasureSelectStyles = {
    // XNG-Change
    print: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `
    },
};


const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    printLabelOverride,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));

    if (
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
    ) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    // XNG-Change: add print label
    return (
        <>
            <StyledWrapper {...styles.print}>
                <StyledWrapper>{(printLabelOverride || labelOverride) ?? translate("U/M")}</StyledWrapper>
                <StyledWrapper>{unitOfMeasure}</StyledWrapper>
            </StyledWrapper>
            <Select
                label={labelOverride ?? translate("U/M")}
                value={unitOfMeasure}
                onChange={uomChangeHandler}
                disabled={disabled}
                data-test-selector="product_unitOfMeasureSelect"
                {...extendedStyles}
                {...otherProps}
            >
                {unitOfMeasures.map(uom => (
                    <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                        {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                        {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                    </option>
                ))}
            </Select>
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
